/**
 * The interstitial shown before a donor leaves for a monastery's own
 * donation page — the moment the spec's hard rule ("do not disguise an
 * external donation as a GOMPA transaction") has to be visible, not just
 * true in the data model. A modal, not a new route: it keeps the donor on
 * the page, which reads calmer than a full navigation for a single
 * confirmation step.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { trackEvent } from '../lib/consent';

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export default function DonationConfirmModal({ monasteryName, monasteryId, donationUrl, onClose }) {
  const { t } = useTranslation();
  const domain = hostnameOf(donationUrl);
  const cancelRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Without this, the page behind the modal keeps scrolling under a
    // donor's thumb on mobile — the modal looks open but the content
    // underneath it is still moving.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Moves focus into the dialog on open — otherwise a keyboard/screen-reader
    // user's focus stays on the trigger button, invisible behind the scrim.
    cancelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  const handleContinue = () => {
    trackEvent('direct_donation_click', { item_id: monasteryId, destination_domain: domain });
    window.open(donationUrl, '_blank', 'noreferrer,noopener');
    onClose();
  };

  return (
    <div
      className="modal-scrim"
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--overlay)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--space-lg)', zIndex: 200,
      }}
    >
      <div
        className="card card__pad"
        role="dialog"
        aria-modal="true"
        aria-labelledby="donation-modal-title"
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth: 440, width: '100%', background: 'var(--surface)' }}
      >
        <h2 id="donation-modal-title" className="display" style={{ fontSize: 20, marginBottom: 'var(--space-sm)' }}>
          {t('donation.modalTitle', { name: monasteryName, defaultValue: `Support ${monasteryName} directly` })}
        </h2>
        <p className="subtle" style={{ marginBottom: 'var(--space-md)' }}>
          {t('donation.modalBody', "You're being directed to the monastery's official donation channel.")}
        </p>
        <p style={{ marginBottom: 'var(--space-lg)', fontSize: 14 }}>
          <strong>{t('donation.destinationLabel', 'Donation destination')}:</strong> {domain}
        </p>
        <p className="subtle" style={{ marginBottom: 'var(--space-xl)', fontSize: 13 }}>
          {t('donation.feeDisclaimer', {
            name: monasteryName,
            defaultValue: `100% of your donation goes to ${monasteryName}.`,
          })}
        </p>
        <div className="row" style={{ gap: 'var(--space-sm)' }}>
          <button ref={cancelRef} type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={handleContinue}>
            {t('donation.continueButton', { domain, defaultValue: `Continue to ${domain}` })}
            <Icon name="external" size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
